import type {
  CollectedLeadFormFields,
  LeadFormContractIssue,
  LeadFormContractValidationResult,
  LeadFormDeliveryContext,
  LeadFormPostalSource,
  LeadFormType,
  ValidateLeadFormContractInput,
} from "./types";

import { collectLeadFormFields } from "./collect-lead-form-fields";
import {
  isLeadFormDeliveryContext,
  isLeadFormType,
  isReservedLeadFormFieldName,
  LEAD_FORM_CONTRACTS,
} from "./form-contracts";
import { getRequiredLeadFormFieldNames } from "./get-required-lead-form-field-names";
import { POSTAL_CODE_FIELD_NAME } from "./lead-form-field-names";

type LeadFormContract = (typeof LEAD_FORM_CONTRACTS)[LeadFormType];

type PostalSourceIssues = {
  errors: LeadFormContractIssue[];
  warnings: LeadFormContractIssue[];
};

function collectUnknownIdentityIssues({
  deliveryContext,
  formType,
}: ValidateLeadFormContractInput): LeadFormContractIssue[] {
  const issues: LeadFormContractIssue[] = [];

  if (formType && !isLeadFormType(formType)) {
    issues.push({
      code: "unknown-form-type",
      fieldName: "formType",
      message: `Unknown form type "${formType}".`,
    });
  }

  if (deliveryContext && !isLeadFormDeliveryContext(deliveryContext)) {
    issues.push({
      code: "unknown-delivery-context",
      fieldName: "deliveryContext",
      message: `Unknown delivery context "${deliveryContext}".`,
    });
  }

  return issues;
}

function collectMissingRequiredFieldIssues({
  collectedFieldNames,
  deliveryContext,
  formType,
}: {
  collectedFieldNames: Set<string>;
  deliveryContext: LeadFormDeliveryContext;
  formType: LeadFormType;
}): LeadFormContractIssue[] {
  const requiredFieldNames = getRequiredLeadFormFieldNames({
    deliveryContext,
    formType,
  });

  return requiredFieldNames.flatMap((fieldName) => {
    if (collectedFieldNames.has(fieldName)) {
      return [];
    }

    return [
      {
        code: "missing-required-field" as const,
        fieldName,
        message: `This form is missing required field "${fieldName}".`,
      },
    ];
  });
}

function collectPostalSourceIssues(
  postalSource: LeadFormPostalSource,
  collected: CollectedLeadFormFields,
): PostalSourceIssues {
  const errors: LeadFormContractIssue[] = [];
  const warnings: LeadFormContractIssue[] = [];

  if (
    postalSource === "authoredVisibleInput" &&
    !collected.hasVisiblePostalCode
  ) {
    errors.push({
      code: "postal-source-mismatch",
      fieldName: POSTAL_CODE_FIELD_NAME,
      message:
        "National forms must include a visible Postal Code step so visitors can enter a ZIP.",
    });
  }

  if (postalSource === "runtimeInjected" && collected.hasVisiblePostalCode) {
    errors.push({
      code: "postal-source-mismatch",
      fieldName: POSTAL_CODE_FIELD_NAME,
      message:
        "Local / office forms supply ZIP from the office. Remove the visible Postal Code step.",
    });
  }

  return { errors, warnings };
}

function collectInvalidOptionValueIssues(
  contract: LeadFormContract,
  collected: CollectedLeadFormFields,
): LeadFormContractIssue[] {
  const issues: LeadFormContractIssue[] = [];

  for (const [fieldName, values] of Object.entries(
    collected.optionValuesByFieldName,
  )) {
    const allowedValues =
      contract.fieldOptionValues[
        fieldName as keyof typeof contract.fieldOptionValues
      ];

    if (!allowedValues) {
      continue;
    }

    const allowed = new Set<string>(allowedValues);

    for (const value of values) {
      if (!allowed.has(value)) {
        issues.push({
          code: "invalid-option-value",
          fieldName,
          message: `"${value}" is not an allowed value for "${fieldName}".`,
        });
      }
    }
  }

  return issues;
}

function collectReservedFieldNameIssues(
  contract: LeadFormContract,
  collected: CollectedLeadFormFields,
): LeadFormContractIssue[] {
  return collected.choiceFieldNames.flatMap((fieldName) => {
    if (
      !isReservedLeadFormFieldName(fieldName) ||
      fieldName in contract.fieldOptionValues
    ) {
      return [];
    }

    return [
      {
        code: "reserved-field-name" as const,
        fieldName,
        message: `"${fieldName}" is reserved for built-in lead form fields. Choose a custom field name instead.`,
      },
    ];
  });
}

function validateLeadFormContract({
  deliveryContext,
  formType,
  steps,
}: ValidateLeadFormContractInput): LeadFormContractValidationResult {
  const errors: LeadFormContractIssue[] = [];
  const warnings: LeadFormContractIssue[] = [];

  if (!formType && !deliveryContext) {
    return { errors, warnings };
  }

  errors.push(...collectUnknownIdentityIssues({ deliveryContext, formType }));

  if (
    !isLeadFormType(formType) ||
    !isLeadFormDeliveryContext(deliveryContext)
  ) {
    return { errors, warnings };
  }

  const contract = LEAD_FORM_CONTRACTS[formType];
  const collected = collectLeadFormFields(steps);
  const postalIssues = collectPostalSourceIssues(
    contract.postalSource[deliveryContext],
    collected,
  );

  errors.push(
    ...collectMissingRequiredFieldIssues({
      collectedFieldNames: new Set(collected.fieldNames),
      deliveryContext,
      formType,
    }),
    ...postalIssues.errors,
    ...collectInvalidOptionValueIssues(contract, collected),
    ...collectReservedFieldNameIssues(contract, collected),
  );
  warnings.push(...postalIssues.warnings);

  return { errors, warnings };
}

export { validateLeadFormContract };
